import { parsePreferences } from "../../functions";

export const createEmptyMenuCart = () => ({
  qty: 0,
  total: 0,
  desc: "",
  items: [],
});

export const flattenMenuItems = (menu) => {
  if (!menu?.content || !Array.isArray(menu.content)) {
    return [];
  }

  return menu.content.flatMap((section) => (Array.isArray(section?.items) ? section.items : []));
};

export const buildMenuItemsMap = (menu) =>
  flattenMenuItems(menu).reduce((acc, item) => {
    if (item?.id) {
      acc[item.id] = item;
    }
    return acc;
  }, {});

const sortCustomizationKeys = (customizations = {}) =>
  Object.keys(customizations).sort((a, b) => Number(a) - Number(b));

export const normalizeMenuCustomizations = (itemCustomizations = {}) =>
  sortCustomizationKeys(itemCustomizations).reduce((acc, key, index) => {
    acc[index] = itemCustomizations[key] || {};
    return acc;
  }, {});

export const serializeMenuCustomizations = (itemCustomizations = {}) => {
  const normalized = normalizeMenuCustomizations(itemCustomizations);

  return Object.keys(normalized).map((key) => normalized[key]);
};

export const removeMenuCustomizationUnit = (itemCustomizations = {}, unitIndex) =>
  sortCustomizationKeys(itemCustomizations).reduce((acc, key) => {
    if (Number(key) === Number(unitIndex)) {
      return acc;
    }

    acc[Object.keys(acc).length] = itemCustomizations[key] || {};
    return acc;
  }, {});

export const buildMissingMenuCustomizations = ({ cartItems = [], customizations = {} }) =>
  (cartItems || []).reduce((acc, item) => {
    const preferenceIds = Object.keys(item?.preferences || {});
    if (!preferenceIds.length) {
      return acc;
    }

    for (let unitIndex = 0; unitIndex < Number(item.qty || 0); unitIndex += 1) {
      const selectedPreferences = customizations?.[item.itemId]?.[unitIndex] || {};
      const missingPreferenceIds = preferenceIds.filter((preferenceId) => !selectedPreferences[preferenceId]);

      if (missingPreferenceIds.length) {
        acc[item.itemId] = {
          ...(acc[item.itemId] || {}),
          [unitIndex]: missingPreferenceIds,
        };
      }
    }

    return acc;
  }, {});

export const buildMenuCart = ({ quantities = {}, itemsMap = {}, language, i18nValue, customizations = {} }) => {
  const cart = createEmptyMenuCart();
  const desc = [];

  Object.keys(quantities || {}).forEach((itemId) => {
    const qty = Number(quantities[itemId] || 0);
    const item = itemsMap[itemId];

    if (!item || qty <= 0) {
      return;
    }

    const name = i18nValue(item, "name", language) || item.name || "";
    const description = i18nValue(item, "description", language) || item.description || "";
    const price = Number(item.price || 0);

    cart.items.push({
      itemId: item.id,
      name,
      type: item.type,
      desc: description,
      price,
      vat: item.vat,
      account: item.account,
      preferences: parsePreferences(item.preferences),
      customizations: serializeMenuCustomizations(customizations[item.id]),
      subAccount: item.subAccount,
      qty,
      total: parseFloat((price * qty).toFixed(2)),
    });

    desc.push(`${qty}x${name}`);
    cart.qty += qty;
    cart.total += parseFloat((price * qty).toFixed(2));
  });

  cart.desc = desc.join(", ");
  return cart;
};

export const serializeMenuCart = (cart) => {
  const nextCart = {
    ...cart,
    items: (cart?.items || []).map((item) => ({
      itemId: item.itemId,
      type: item.type,
      price: item.price,
      vat: item.vat,
      account: item.account,
      customizations: item.customizations || [],
      subAccount: item.subAccount,
      qty: item.qty,
      total: item.total,
    })),
  };

  delete nextCart.desc;
  return nextCart;
};

export const resolveMenuRequestType = (requestType) => {
  if (requestType === "spa" || requestType === "pool_service") {
    return "items_sales";
  }

  return requestType || "room_service";
};

export const buildMenuRequestPayload = ({
  requestType,
  quantities = {},
  cart,
  deliveryPoint = "",
  observations = "",
  requestId,
}) => {
  const requestDate = new Date().toISOString();
  const payload = {
    type: resolveMenuRequestType(requestType),
    data: JSON.stringify({ items: quantities, date: requestDate }),
    cart: JSON.stringify(serializeMenuCart(cart)),
    deliveryPoint,
    observations,
  };

  if (requestId) {
    payload.id = requestId;
  }

  return payload;
};

export const filterMenuItemsBySearch = ({ items = [], searchTerm = "", language, i18nValue }) => {
  if (!searchTerm) {
    return items;
  }

  const normalizedSearch = String(searchTerm).trim().toLowerCase();
  if (!normalizedSearch) {
    return items;
  }

  return items.filter((item) => {
    const name = i18nValue(item, "name", language) || item.name || "";
    const description = i18nValue(item, "description", language) || item.description || "";

    return (
      String(name).toLowerCase().includes(normalizedSearch) ||
      String(description).toLowerCase().includes(normalizedSearch)
    );
  });
};
